<template>
    <div>
        <v-dialog
            v-model="display"
            persistent
            width="750">
            <v-card flat>
                <v-card-title>
                    {{ title }}
                    <v-spacer></v-spacer>
                    <v-menu
                        v-if="form.base_currency_id"
                        offset-y>
                        <template v-slot:activator="{on}">
                            <v-btn
                                icon
                                v-on="on"
                                class="mx-1">
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list width="250" dense>
                            <v-list-item
                                v-if="notEditMode"
                                @click="openExchangeRateLineDialog({ editMode: false })"
                                link>
                                <v-list-item-icon>
                                    <v-icon>mdi-database-plus</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('new_index') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item
                                v-if="form.exchange_rate_lines.length"
                                @click="openExchangeRateLineDialog({ editMode: true, currency: form.exchange_rate_lines })"
                                link>
                                <v-list-item-icon>
                                    <v-icon>mdi-pencil</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('edit_index') }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-card-title>
                <v-card-text>
                    <v-form
                        ref="form"
                        v-model="valid">
                        <v-row>
                            <v-col>
                                <v-menu
                                    ref="menu"
                                    v-model="menu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="form.date"
                                        :rules="rules.date"
                                        dense
                                        :label="$t('date')"
                                        prepend-icon="mdi-calendar"
                                        hide-details="auto"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                    ></v-text-field>
                                    </template>
                                    <v-date-picker @input="menu = false" v-model="form.date" no-title scrollable>
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col>
                                <v-select
                                    :label="$t('currency')"
                                    dense
                                    hide-details="auto"
                                    :rules="rules.base_currency_id"
                                    item-text="name"
                                    item-value="id"
                                    v-model="form.base_currency_id"
                                    :disabled="!!form.exchange_rate_lines.length"
                                    :items="currencies">
                                </v-select>
                            </v-col>
                        </v-row>
                        <v-data-table
                            class="mt-5"
                            dense
                            hide-default-footer
                            disable-pagination
                            v-model="selected.exchangeRate"
                            :headers="headers.exchangeRates"
                            :items="form.exchange_rate_lines">
                             <template v-slot:[`item.name`]="{ item }">
                                {{ `1 `+item.abbreviation+`` }}
                            </template>
                            <template v-slot:[`item.rate`]="{ item }">
                                {{ item.rate+` `+abbreviation }}
                            </template>

                        </v-data-table>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-btn :disabled="!valid || !form.exchange_rate_lines.length" @click="submit" text>{{ $t('save_btn') }}</v-btn>
                    <v-btn @click="close" text>{{ $t('close_btn') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <RetryDialog
            ref="retryDialog"
            @submit="onRetryDialogSubmit"/>
        <ExchangeRateLineDialog
            v-if="exchangeRateLineDialogProps.display"
            :currency="exchangeRateLineDialogProps.currency"
            :currency_id="exchangeRateLineDialogProps.currency_id"
            :abbreviation="exchangeRateLineDialogProps.abbreviation"
            @close="closeExchangeRateLineDialog"
            @submit="onSubmitExchangeRateLine"
            ref="exchangeRateLineDialog"/>
    </div>
</template>
<script>
import ExchangeRateLineDialog from '@/components/accounting/ExchangeRateLineDialog';
import RetryDialog from '@/components/common/RetryDialog';
import { mapActions, mapState } from 'vuex';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
export default {
    name: 'ExchangeRateDialog',
    props: {
        id: {
            type: Number,
            required: false
        }
    },
    components: {
        RetryDialog,
        ExchangeRateLineDialog
    },
    data() {
        return {
            valid: false,
            menu: false,
            display: true,
            idempotentKey: null,
            loading: {
                data: false
            },
            form: {
                id: null,
                root_dimension_id: 1,
                date: new moment().format("YYYY-MM-DD"),
                base_currency_id: null,
                exchange_rate_lines: []
            },
            rules: {
                date: [
                    v => !!v || this.$t('rules_required')
                ],
                base_currency_id: [
                    v => !!v || this.$t('rules_required')
                ]
            },
            headers: {
                exchangeRates: [
                    { text: this.$t('currency'), value: 'name' },
                    { text: this.$t('rate'), value: 'rate' },
                ]
            },
            selected: {
                exchangeRate: []
            },
            exchangeRateLineDialogProps: {
                display: false,
                currency: {},
                currency_id: null,
                abbreviation: null
            }
            
        }
    },
    computed: {
        ...mapState('accounting/currency', ["currencies"]),
        ...mapState('accounting/exchangeRate', ["exchangeRate"]),
        editMode() {
            return !this.notEditMode;
        },
        notEditMode() {
            return !this.id;
        },
        title() {
            if(this.notEditMode) return this.$t('new_exchange_rate');
            else return this.$t('edit_exchange_rate')
        },
        abbreviation() {
            if(this.currencies.length) return this.currencies.find(each => each.id == this.form.base_currency_id).abbreviation;
            else return null;
        }
        
    },
    methods: {
        ...mapActions('accounting/currency', ["fetchCurrencies"]),
        ...mapActions('accounting/exchangeRate', ["fetchExchangeRate", "storeExchangeRate", "updateExchangeRate"]),
        ...mapActions('common/snackBar', ["setSnackBars"]),
        async createdDataFetch() {
            try {
                this.loading.data = true;
                if(this.editMode) {
                    await this.fetchExchangeRate(this.id);
                    this.form = {...this.exchangeRate};
                }
                await this.fetchCurrencies();
                this.loading.data = false;
            } catch(error) {
                let message = error.response ? error.response.data.message : error;
                this.$refs.retryDialog.open({target:"createdDataFetch",message});
            }
        },
        onRetryDialogSubmit(params){
            if(params.target == "createdDataFetch") {
                if(params.retry) this.createdDataFetch();
            }
        },
        submit() {
            this.loading.submit = true;
            if(this.notEditMode) {
                this.form.idempotentKey = this.idempotentKey;
                this.storeExchangeRate(this.form)
                    .then(() => {
                        this.close();
                        this.$emit('submit');
                        this.setSnackBars({color: "success", message: this.$t("record_save_success")});
                    })
                    .catch(error => {
                        this.loading.submit = false;
                        let message = error.response ? error.response.data.message : error;
                        this.setSnackBars({color: "error", message});
                    })
            } else {
                this.updateExchangeRate(this.form)
                    .then(() => {
                        this.close();
                        this.$emit('submit');
                        this.setSnackBars({color: "success", message: this.$t("record_update_success")});
                    })
                    .catch((error) => {
                        this.loading.submit = false;
                        let message = error.response ? error.response.data.message : error;
                        this.setSnackBars({color: "error", message});
                    })
            }
        }, 
        openExchangeRateLineDialog({ editMode, currency }) {
            editMode ? this.exchangeRateLineDialogProps.currency = JSON.parse(JSON.stringify(currency)) : this.exchangeRateLineDialogProps.currency = [];
            this.exchangeRateLineDialogProps.currency_id = this.form.base_currency_id;
            this.exchangeRateLineDialogProps.abbreviation =  this.abbreviation;
            this.exchangeRateLineDialogProps.display = true;
        },
        closeExchangeRateLineDialog(){
            this.exchangeRateLineDialogProps.display = false;
        },
        onSubmitExchangeRateLine(params) {
            this.form.exchange_rate_lines = params;
            this.closeExchangeRateLineDialog();
        },
        close() {
            this.$emit('close');
        }
    },
    created () {
        this.createdDataFetch();
        this.idempotentKey = uuid();
    },
}
</script>